import type { LegalTextRun } from './legal-content';

export type TermsTextRun = LegalTextRun;

export type TermsContentBlock =
  | { type: 'title' | 'date' | 'heading' | 'paragraph'; runs: TermsTextRun[] }
  | { type: 'spacer' };

export const TERMS_CONTENT_BLOCKS: TermsContentBlock[] = [
  {
    type: 'title',
    runs: [
      {
        text: 'Terms and Conditions for CareCal',
      },
    ],
  },
  {
    type: 'date',
    runs: [
      {
        text: 'August 31, 2026',
        italic: true,
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Thank you for using CareCal. These Terms and Conditions (“Terms”) are a legal agreement between you and BookLilliCares LLC dba CareCal (“CareCal”, “we” or “us”) and govern your access to and use of the CareCal Site and/or the CareCal Services/Platform (as defined below).',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Please read these Terms before using the Site or the Services/Platform. ',
      },
      {
        text: 'By using the Site or the Services/Platform, or by clicking to accept or agree to the Terms when this option is made available to you, you hereby represent, warrant, understand, agree to and accept these Terms in their entirety whether or not you register as a user of the Site or Services/Platform. These Terms include the CareCal Privacy Policy (below), which is incorporated herein.',
        bold: true,
      },
      {
        text: ' ',
      },
      {
        text: 'If you do not want to agree ',
      },
      {
        text: 'to ',
      },
      {
        text: 'these Terms or the Privacy Policy, you must not access or use the Site or the Services/Platform.  ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'CareCal reserves the right, at its reasonable and sole discretion, to modify or replace these Terms at any time. The most current version of these Terms will be accessible through the Site or the Platform. You shall be responsible for reviewing and becoming familiar with any such modifications. If a revision to the Terms, in our sole discretion, is material, we will notify you by contacting you either through the email address associated with your Account or another messaging system you have associated with your Account. Use of the Platform by you after any modification to the Terms constitutes your acceptance of the Terms as modified.',
      },
      {
        text: ' ',
      },
      {
        text: 'However, any changes to the dispute resolution provisions ',
      },
      {
        text: 'included in the Terms ',
      },
      {
        text: 'will not apply to any disputes for which the parties have actual notice on or before the date the change is posted on the ',
      },
      {
        text: 'Site',
      },
      {
        text: '.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'For purposes of these Terms, the “Site” shall mean ',
      },
      {
        text: 'www.mycarecal.com',
        href: 'https://www.mycarecal.com',
      },
      {
        text: ' and any other websites, web pages, mobile applications and mobile websites operated by CareCal in the United States, and the “Services” or “Platform” shall mean any of the various services',
      },
      {
        text: ' or products',
      },
      {
        text: ' that CareCal provides through the Site.',
      },
    ],
  },
  {
    type: 'spacer',
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'ACCESSING THE SITE AND ACCOUNT SECURITY',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We may from time to time add new features to the ',
      },
      {
        text: 'Site or ',
      },
      {
        text: 'Services, substitute a new service for one of the existing Services, or discontinue or suspend one or any part of the existing Services. Under no circumstances will we be liable for suspension or discontinuation of the Services or any portion thereof, or if all or any part of the Services are unavailable at any time or for any period for any reason. From time to time, we may restrict user access, to some parts of the Services or the entire ',
      },
      {
        text: 'Site',
      },
      {
        text: '. Any use of new features or services will be covered by these Terms of Use.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Certain features or Services may require account registration or other information. It is a condition of your use of the Services that all the information you provide on the ',
      },
      {
        text: 'Site ',
      },
      {
        text: 'or to access the Services is correct, current, and complete. If you choose, or are provided with, a username, password, or any other piece of information as part of our security procedures, you must treat such information as confidential, and you must not disclose it to any other person or entity. You also acknowledge that your account is personal to you and agree not to provide any other person with access to the Services or portions of it using your username, password, or other security information. You agree to notify us immediately of any unauthorized access to or use of your username or password or any other breach of security. You also agree to ensure that you exit from your account at the end of each session. You should use particular caution when accessing your account from a public or shared computer so that others are not able to view or record your password or other personal information.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We have the right to disable any username, password, or other identifier, whether chosen by you or provided by us, at any time in our sole discretion for any or no reason, including if, in our opinion, you have violated any provision of these Terms of Use.',
      },
    ],
  },
  {
    type: 'spacer',
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'SCOPE OF SERVICES',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The mission of the CareCal care recipient/client - caregiver scheduling platform is to serve care recipient/clients (“care recipient/client User” and sometimes referred generally as “User”) and caregivers (“Caregiver User” and sometimes referred generally as “User”) by providing the freedom to choose who they work with, how they pay them, and to sync up in the most efficient way and through what is most fundamental to care recipient/client-caregiver matches: an aligned schedule. CareCal will save time and headaches for caregivers and care recipient/clients, increase childcare',
      },
      {
        text: ',',
      },
      {
        text: ' eldercare',
      },
      {
        text: ', and related',
      },
      {
        text: ' coverage opportunities for care recipient/clients, and lend to increased, planned income for gig workers in childcare',
      },
      {
        text: ', eldercare, and ',
      },
      {
        text: 'other related caregiving fields',
      },
      {
        text: ' ',
      },
      {
        text: '.  ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'LIMITATIONS OF OUR SERVICES',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Our Services/Platform solely provides a schedule viewing and booking tool for care recipient/clients and caregivers',
      },
      {
        text: '.',
      },
      {
        text: ' ',
      },
      {
        text: 'CareCal ',
      },
      {
        text: 'is not a',
      },
      {
        text: ' referral, matching or placement agency or service. Carecal make ',
      },
      {
        text: 'does not screen, perform background checks, train, or ',
      },
      {
        text: 'otherwise vet any Caregiver User. ',
      },
      {
        text: 'Carecal ',
      },
      {
        text: 'makes no recommendations or representations about ',
      },
      {
        text: 'Users',
      },
      {
        text: ', including any representations about the suitability, reliability, integrity, qualifications or trustworthiness of any ',
      },
      {
        text: 'particular User.',
      },
      {
        text: ' You should make your own assessments of the',
      },
      {
        text: ' Users',
      },
      {
        text: ' you decide to interact with, provide services to and/or engage.',
      },
      {
        text: ' ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'By agreeing to these Terms, you understand and agree that the Platform does not provide childcare, elder care, pet care, or other care services, and is not a caregiving marketplace, an agency, or matching service of any kind. CareCal provides a technology platform for care recipient/clients and caregivers to share schedules and book with individuals of their own choosing. ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The information published on a Caregiver User’s profile or shared by the care recipient/client User in the booking process is not reviewed or confirmed by CareCal. Likewise, CareCal does not review or regulate reviews on Caregiver Users profiles. CareCal makes no warranties as to the accuracy or completeness of any information on Caregiver User’s profile or reviews.',
      },
      {
        text: ' ',
      },
      {
        text: 'CareCal is not involved in the transactions between Users. ',
      },
      {
        text: 'CareCal is not in control of the individual caregivers or care recipient/clients, their performance, responsiveness, or their personal methodologies',
      },
      {
        text: '. Carecal likewise ha',
      },
      {
        text: 's ',
      },
      {
        text: 'no control over the timing, ',
      },
      {
        text: 'quality',
      },
      {
        text: ', failure to prov',
      },
      {
        text: 'ide',
      },
      {
        text: ', or any other aspect of the services provided by a ',
      },
      {
        text: 'Caregiving User. All Users are solely responsible for their interactions with each other, whether through the Platform or offline.',
      },
      {
        text: ' ',
      },
      {
        text: 'Users ',
      },
      {
        text: 'must resolve any issues, disputes or concerns directly with each other, including any issues regarding payments to ',
      },
      {
        text: 'a Caregiving User',
      },
      {
        text: ' or services received by ',
      },
      {
        text: 'a Care Recipient',
      },
      {
        text: '. ',
      },
      {
        text: 'Users agree that Carecal will not be responsible for any liability incurred as the result of such interaction',
      },
      {
        text: ' pursuant to the Terms herein',
      },
      {
        text: '.  ',
      },
      {
        text: ' ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Carecal is no',
      },
      {
        text: 't an employment service and does not serve as an employer for any User. ',
      },
      {
        text: 'Users are solely responsible for complying with any and all labor, ',
      },
      {
        text: 'employment',
      },
      {
        text: ', or other laws related to caregiver services. ',
      },
      {
        text: 'By agreeing to these Terms, y',
      },
      {
        text: 'ou acknowledge and agree that you are solely responsible for evaluating whether a ',
      },
      {
        text: 'User',
      },
      {
        text: ' is appropriate for your specific needs, including verifying any information provided and conducting your own due diligence before entering into ',
      },
      {
        text: 'and continuing ',
      },
      {
        text: 'any engagement.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'ELIGIBILITY',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Our Site and Platform are eligible for users age 18 or older or if you are between the ages of 13 and 17 and comply with the Teen User provisions below. No one under age 13 may provide any personal information to the ',
      },
      {
        text: 'Site',
      },
      {
        text: ' or on the Platform. We do not knowingly collect personal information from children under 13. If you are under 13, do ',
      },
      {
        text: 'not use or provide any information on this ',
      },
      {
        text: 'Site',
      },
      {
        text: ' or through any of its features, register on the Platform, or otherwise provide any information about yourself to us, including your name, address, telephone number, or email address. If we learn we have collected or received personal information from a child under 18 without verification of parental consent pursuant to these Terms, we will delete that information. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'TEEN USERS',
      },
      {
        text: ' ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'If you are between 13 and 17 years of age and would like to use the ',
      },
      {
        text: 'Site',
      },
      {
        text: ' or Platform (a “Teen User”), you may register to use the services only if your parent or legal guardian consents to your registration through the services and becomes the primary account holder or administrator of your account (a “Guardian”).',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The Guardian is responsible for the Teen User’s account, including all activity that occurs under the account, and for ensuring that the Teen User’s use of the Platform complies with these Terms and our Privacy Policy. By creating or approving an account for a Teen User, the Parent User represents and warrants that they are the parent or legal guardian of the Teen User and consents to the Teen User’s use of the Platform and to the collection and use of the Teen User’s information as described in the Privacy Policy.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Once the Guardian account is established, the Guardian may invite, register, or otherwise add a Teen User to the account as an authorized user. The Guardian will be solely responsible for all billing associated with the Teen User account. The Guardian will have the option to access to all communications between the Teen User and other Users as well as a copy of all communications we send to the Teen User. Guardians may report inappropriate message content to us and may terminate their Teen User’s account at any time, subject to these Terms. We reserve the right to require additional verification of parental or guardian status at any time and to suspend or terminate accounts where we believe such consent has not been validly obtained.',
      },
      {
        text: ' ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Caregiver Referral through the platform',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'CareCal may partner with caregiver, nanny, or sitter agencies (“Agencies”) to provide Users access to a caregiver network within the Platform (“agency referral services”). Agency referral services are provided solely for the User’s convenience and CareCal simply provides the scheduling tool to link Users with caregivers from the agency referral services. Users will have the option to opt-in for the agency referral services, if available, through the Platform. Agency referral services are entirely optional, and will ',
      },
      {
        text: 'require User’s separate consent, and are subject to the Agencies’ terms of service and privacy policy. You understand and agree that such agency referral services are not provided by CareCal, and CareCal does not control or endorse and does not make any representations or warranties regarding such agencies, the services they provide, or the suitability, reliability, timeliness, or accuracy of the services provided by caregivers identified by the agency referral services. There is no guarantee that by using the agency referral services that a caregiver will be identified or secured. It is Users sole responsibility to vet, screen, perform background checks, or any other action necessary to confirm suitability of caregivers identified by the agency referral services. YOU UNDERSTAND AND AGREE THAT USE OF SUCH AGENCY REFERRAL SERVICE AND CONTENT IS AT YOUR OWN RISK. CARECARL IS NOT RESPONSIBLE OR LIABLE IN ANY MANNER FOR THE AGENCIES’ SERVICES, OR FOR ANY LOSS OR DAMAGE INCURRED AS A RESULT THEREOF, AND CARECAL EXPRESSLY DISCLAIM, AND YOU HEREBY EXPRESSLY RELEASE CARECAL FROM ANY AND ALL LIABILITY WHATSOEVER FOR ANY CLAIMS, LIABILITIES, AND/OR DAMAGES ARTISING FROM AND/OR IN ANY WAY RELATED TO THE AGENCIES’ SERVICES.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'For Educational and Informational Purposes Only',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The information contained on this',
      },
      {
        text: ' Site',
      },
      {
        text: ' and ',
      },
      {
        text: 'any information ',
      },
      {
        text: 'available for download through this ',
      },
      {
        text: 'Site',
      },
      {
        text: ', including any purchased products,',
      },
      {
        text: ' are for educational and informational purposes only. We do not warrant the accuracy, completeness, or usefulness of the information provided on the Website',
      },
      {
        text: ' or through our Services',
      },
      {
        text: '. Any reliance you place on such information is strictly at your own risk. The information contained on this Website and the resources available for download through this Website is not intended as, and shall not be understood or construed as legal, financial, tax, medical, health, or any other professional advice. We disclaim all liability and responsibility arising from any reliance placed on such materials by you or any other visitor to the Website, or by anyone who may be informed of any of its contents.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Use of Paid Courses and Associated Materials',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'When you purchase a course or any other offering on our',
      },
      {
        text: ' Site',
      },
      {
        text: ', the Company grants you a limited, personal, non-exclusive, non-transferable license to use our courses, programs, and associated material (collectively the “Courses”) for your own personal use. Except as otherwise provided, you acknowledge and agree that you have no right to modify, edit, copy, reproduce, create derivative works of, reverse engineer, alter, enhance or in any way exploit any of the Courses in any manner.',
      },
      {
        text: '\n',
      },
      {
        text: '\nBy ordering or participating in Courses, you agree that the Courses you purchase or download may only be used by you for your personal use and may not be sold or redistributed without the express written consent of the Company.',
      },
      {
        text: '\n',
      },
      {
        text: '\nBy ordering or participating in Courses, you further agree that you shall not create any derivative work based upon the Courses and you shall not offer any competing products or services based upon any information contained in the Courses.',
      },
    ],
  },
  {
    type: 'spacer',
  },
  {
    type: 'spacer',
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Intellectual Property',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'This Site and Services and all materials contained therein are the property of CareCal and protected by copyright, trademark, and other intellectual property laws. The Website and the materials available on this Site are provided solely for your personal, non-commercial use. ',
      },
      {
        text: 'You must not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on ou',
      },
      {
        text: 'r Site or Platform. ',
      },
      {
        text: ' ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Fees, Termination, and Refunds',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'PLATFORM',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'You agree to pay all applicable fees related to your use of Platform which are provided',
      },
      {
        text: ' here',
      },
      {
        text: ' (',
      },
      {
        text: 'www.mycarecal.com',
        href: 'https://www.mycarecal.com',
      },
      {
        text: ').',
      },
      {
        text: ' ',
      },
      {
        text: 'By signing up for a subscription with Carecal, you agree to the specific terms of the subscription ',
      },
      {
        text: 'you',
      },
      {
        text: ' register for, including the price and billing frequency. Unless stated otherwise, all subscriptions are set to automatically renew at the end of each billing cycle. You will be charged the then-current rate for the renewal period unless you cancel your subscription before the renewal date.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'By providing a payment method, you expressly authorize us to charge the applicable fees on said payment method as well as taxes and other charges incurred thereto at regular intervals, which vary depending on whether your subscription is yearly or monthly.',
      },
      {
        text: ' ',
      },
      {
        text: 'You also agree that Carecal is authorized to share any payment information and instructions required to complete the payment transactions with its third-party payment service providers (e.g., credit card transaction processing, merchant settlement, and related services).',
      },
      {
        text: ' ',
      },
      {
        text: 'You agree to provide current, complete, and accurate payment and billing information. If your payment is declined or otherwise unsuccessful, your access to the ',
      },
      {
        text: 'Platform ',
      },
      {
        text: 'will be removed. ',
      },
      {
        text: 'Subscriptions can be cancelled any time through the Manage Plan tab inside the Platform dashboard. Please be aware that after ',
      },
      {
        text: 'you cancel',
      },
      {
        text: ' your subscription, you may not be able to use or access the Platform.',
      },
      {
        text: ' ',
      },
      {
        text: 'Our collection, use, and retention of your information following cancellation are governed by our ',
      },
      {
        text: 'Privacy Policy',
      },
      {
        text: '.',
      },
      {
        text: ' ',
      },
      {
        text: 'You will not be entitled to any refund of fees that you have paid.',
        bold: true,
        underline: true,
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'COURSES',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Any purchase of our Courses ',
        bold: true,
        underline: true,
      },
      {
        text: 'is',
        bold: true,
        underline: true,
      },
      {
        text: ' final and non-refundable',
        bold: true,
        underline: true,
      },
      {
        text: '. By completing your purchase of a Course, you acknowledge and agree that no cancellations, refunds, or chargebacks will be accepted under any circumstances, including but not limited to dissatisfaction with the content. You understand that the value of your purchase is in the immediate delivery of proprietary materials',
      },
      {
        text: ' and ',
      },
      {
        text: 'digital content, which cannot be reversed, returned, or undone once accessed. Please review all product details and terms carefully before purchasing. If you have any questions about ',
      },
      {
        text: 'a Course',
      },
      {
        text: ', you are encouraged to contact us before completing your purchase with any questions. ',
      },
    ],
  },
  {
    type: 'spacer',
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Prohibited Uses',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'You may use the Site and/or Services only for lawful purposes and in accordance with these Terms. ',
      },
      {
        text: 'You agree not t',
      },
      {
        text: 'o use the Site or Services: (i) ',
      },
      {
        text: 'i',
      },
      {
        text: 'n any way that violates any applicable federal, state, local, or international law or regulation (including, without limitation, any laws regarding the export of data or software to and from the US or other countries)',
      },
      {
        text: '; (ii) f',
      },
      {
        text: 'or the purpose of exploiting, harming, or attempting to exploit or harm minors in any way by exposing them to inappropriate content, asking for personally identifiable information, or otherwise',
      },
      {
        text: '; (iii) t',
      },
      {
        text: 'o transmit, or procure the sending of, any advertising or promotional material, including any "junk mail," "chain letter," "spam," or any other similar solicitation',
      },
      {
        text: '; or (iv) t',
      },
      {
        text: "o engage in any other conduct that restricts or inhibits anyone's use or enjoyment of th",
      },
      {
        text: 'e Site or Platform',
      },
      {
        text: ', or which, as determined by us, may harm ',
      },
      {
        text: 'Carecal or the users of the Site or Platform',
      },
      {
        text: ', or expose them to liability.',
      },
      {
        text: ' ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Any unauthorized use may result in the deletion of your CareCal account or a block from the Platform.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Content Standards',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The content that Users post and share in the Platform is referred to in these Terms as “User Content.” User Content may include the User’s Link, messages and replies, and multimedia, as well as information that identifies you, such as your name, contact information, and your picture. ',
      },
      {
        text: 'Users are solely',
      },
      {
        text: ' responsible for User Content. ',
      },
    ],
  },
  {
    type: 'spacer',
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'You agree not to post, share, or otherwise communicate any User Content that includes images of another person without their permission (or, for minors, without permission from a parent or legal guardian); infringes or violates another party’s intellectual property rights; discloses or collects another person’s personal information without their consent; suggests or implies endorsement by',
      },
      {
        text: ' Carecal',
      },
      {
        text: ' where none exists; is unlawful, false, misleading, defamatory, abusive, obscene, or otherwise objectionable; promotes discrimination, harassment, threats, or harm of any kind; is intended to harass, annoy, or disrupt other users; involves spam, phishing, or other unsolicited or deceptive ',
      },
      {
        text: 'communications; contains viruses, malware, or other harmful code; interferes with or disrupts the operation of the Platform; uses bots, scripts, or automated methods to access or interact with the Platform; attempts to bypass the Platform, including avoiding fees or conducting transactions outside the Platform; or otherwise violates applicable laws or the rights of others.',
      },
    ],
  },
  {
    type: 'spacer',
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'CareCal is not under any obligation to screen or monitor User Content. However, CareCal reserves the right to review User Content in CareCal’s sole discretion to ensure compliance with these Terms and applicable laws, and to take any action CareCal deems necessary, without notice, in response to a User’s violation of these Terms, including removing User Content, suspending or terminating a User’s account, and taking any appropriate legal action. No delay by CareCal in exercising or failure to exercise any part of these Terms will operate or be construed as a waiver thereof.',
      },
    ],
  },
  {
    type: 'spacer',
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'You ',
      },
      {
        text: 'have the right, and hereby grant, to CareCal, its Affiliates, licensees and successors, an irrevocable, perpetual, non-exclusive, fully paid-up, transferable, worldwide license to use, copy, perform, display, reproduce, adapt, modify and distribute your User Content',
      },
      {
        text: ',',
      },
      {
        text: ' and to prepare derivative works of, or incorporate into other works, such User Content, and to grant and authorize sublicenses of the foregoing. ',
      },
    ],
  },
  {
    type: 'spacer',
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Other Websites',
      },
      {
        text: ' AND THIRD-PARTY LINKS',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The Site may contain links to third party websites. These links are provided solely as a convenience to you. By linking to these websites, we do not create or have an affiliation with or sponsor such third party websites unless otherwise identified in these Terms. The inclusion of links within our Website does not constitute any endorsement, guarantee, warranty, or recommendation of such third party websites. CareCal has no control over the legal documents and privacy practices of third party websites, and by using them, you may be giving those third parties permission to use or control your information in ways CareCal would not. As such, you access any third-party websites at your own risk. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Legal Compliance',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'You agree to comply with all applicable domestic and international laws, statutes, ordinances, and regulations regarding your use of the Site',
      },
      {
        text: ' and Platform',
      },
      {
        text: '. CareCal reserves the right to investigate complaints or reported violations of our Terms and to take any action we deem appropriate, including but not limited to canceling your User account, reporting any suspected unlawful activity to law enforcement officials, ',
      },
      {
        text: 'regulators, or other third parties. You agree to promptly notify CareCal in the event that you are made aware of any unauthorized or illegal use of the Site or Services.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Reliance on Information Posted',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'All information on the Site, including but not limited to blog posts and tips, are on an information-only basis. We do not warrant the accuracy, completeness, or usefulness of this information. None of the information is professional advice or made specifically for your family. We disclaim all liability and responsibility arising from any reliance placed on such materials by you or any other visitor to the Site, or by anyone who may be informed of any of its contents.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Changes to the Website',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We may update the content on th',
      },
      {
        text: 'e Site or Platform',
      },
      {
        text: ' from time to time, but its content is not necessarily complete or up-to-date. Any of the material on the ',
      },
      {
        text: 'Site or Platform',
      },
      {
        text: ' may be out of date at any given time, and we are under no obligation to update such material.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Geographic Restrictions',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The owner of the',
      },
      {
        text: ' Site ',
      },
      {
        text: 'is based in the State of',
      },
      {
        text: ' Rhode Island',
      },
      {
        text: ' in the United States. We provide this',
      },
      {
        text: ' Site and the Services',
      },
      {
        text: ' for use only by persons located in the United States. We make no claims that th',
      },
      {
        text: 'e Site',
      },
      {
        text: ' or any of its content is accessible or appropriate outside of the United States. Access to the',
      },
      {
        text: ' Site',
      },
      {
        text: ' ',
      },
      {
        text: 'may not be legal by certain persons or in certain countries. If you access the',
      },
      {
        text: ' Site',
      },
      {
        text: ' from outside the United States, you do so on your own initiative and are responsible for compliance with local laws.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Disclaimer of Warranties',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'You understand that we cannot and do not guarantee or warrant that files available for downloading from the internet or the',
      },
      {
        text: ' Site or Platform',
      },
      {
        text: ' ',
      },
      {
        text: 'will be free of viruses or other destructive code. You are responsible for implementing sufficient procedures and checkpoints to satisfy your particular requirements for anti-virus protection and accuracy of data input and output, and for maintaining a means external to our site for any reconstruction of any lost data. TO THE FULLEST EXTENT PROVIDED BY LAW, WE WILL NOT BE LIABLE FOR ANY LOSS OR DAMAGE CAUSED BY A DISTRIBUTED DENIAL-OF-SERVICE ATTACK, VIRUSES, OR OTHER TECHNOLOGICALLY HARMFUL MATERIAL THAT MAY INFECT YOUR COMPUTER EQUIPMENT, COMPUTER PROGRAMS, DATA, OR OTHER PROPRIETARY MATERIAL DUE TO ',
      },
      {
        text: 'YOUR USE OF THE',
      },
      {
        text: ' SITE',
      },
      {
        text: ' OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE',
      },
      {
        text: ' SITE',
      },
      {
        text: ' ',
      },
      {
        text: 'OR YOUR DOWNLOADING OF ANY MATERIAL POSTED ON IT, OR ON AN',
      },
      {
        text: 'Y SITE',
      },
      {
        text: ' ',
      },
      {
        text: 'LINKED TO IT.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'YOU EXPRESSLY AGREE THAT THE USE OF THE SITE OR SERVICES/PLATFORM IS AT YOUR SOLE RISK',
      },
      {
        text: ', INCLUDING BUT NOT LIMITED TO',
      },
      {
        text: ', ALL RISKS ASSOCIATED WITH ONLINE OR OFFLINE INTERACTIONS WITH OTHERS',
      },
      {
        text: '. THIS SITE, SERVICES/PLATFORM AND RESOURCES THEREIN ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS. THE CARECAL PLATFORM EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR NON-INFRINGEMENT. NEITHER CARECAL NOR ANY PERSON ASSOCIATED WITH CARECAL MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF THE WEBSITE. ',
      },
      {
        text: 'C',
      },
      {
        text: 'arecal makes no warranty that services will be uninterrupted, secure or error-free. You understand and agree that any material and/or data downloaded or otherwise obtained through the use of CareCal platform or site is at your own discretion and risk and that you will be solely responsible for any damage to your own computer system or loss of data that results from the download of such material and/or data.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Limitation on Liability',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'TO THE FULLEST EXTENT PROVIDED BY LAW, IN NO EVENT WILL CARECAL, ITS AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE SITE, THE SERVICES/PLATFORM, ANY WEBSITES LINKED TO IT, ANY CONTENT ON THE SITE OR SERVICES/PLATFORM, INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO, PERSONAL INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF PROFITS, LOSS OF BUSINESS OR ANTICIPATED SAVINGS, LOSS OF USE, LOSS OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE, EVEN IF FORESEEABLE.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Indemnification and Release',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'You agree to defend, indemnify and hold CareCal, its affiliates, licensor, and service providers, and its respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns harmless from and against any and all damages, costs, claims, or demands, including reasonable attorneys’ fees, made by any third party due to or arising from or relating to your use of the CareCal Site and Services.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Governing Law and Jurisdiction',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'These Terms will be governed by the laws of the State ',
      },
      {
        text: 'of ',
      },
      {
        text: 'Rhode Island. The state and federal courts located in Rhode Island will have exclusive jurisdiction over any case or controversy arising from or relating to this agreement, use of this Site, or any products or services provided by CareCal or disputes arising out of or relating to your use of the Site or Services. You hereby unconditionally and irrevocably consent to the personal and subject matter jurisdiction of the federal and state courts of the State of Rhode Island for purposes of any claim or action arising out of or relating to these Terms.',
      },
      {
        text: '\n',
      },
      {
        text: '\nBy accessing or using the Site and Services, you consent irrevocably to personal jurisdiction in such courts in relation to any matter in connection with your use of this Site or use of services provided by or products purchased from CareCal and waive any defense of forum non conveniens. Through your access and use of this Site, you are deemed to have knowingly and voluntarily waived any right to a trial by jury in any case or controversy related to these Terms, use of this Site, or any services provided by or products purchased from CareCal.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Arbitration',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: "At CareCal's sole discretion, it may require you to submit any disputes arising from these Terms or use of the Site or Services, including disputes arising from or concerning their interpretation, violation, invalidity, non-performance, or termination, to final and binding arbitration under the Rules of Arbitration of the American Arbitration Association applying to Rhode Island law.",
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Waiver and Severability',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'No waiver by CareCal of any term or condition set out in these Terms shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition, and any failure of CareCal to assert a right or provision under these Terms shall not constitute a waiver of such right or provision.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'If any provision of these Terms is held by a court or other tribunal of competent jurisdiction to be invalid, illegal, or unenforceable for any reason, such provision shall be eliminated or limited to the minimum extent such that the remaining provisions of the Terms will continue in full force and effect. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'ENTIRE AGREEMENT ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The Terms and Conditions and Privacy Policy constitute the sole and entire agreement between you and CareCal regarding the Site and Services and supersede all prior and contemporaneous understandings, agreements, representations, and warranties, both written and oral, regarding the ',
      },
      {
        text: 'Site and Services',
      },
      {
        text: '. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Your Comments and Concerns',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'This website is owned and operated by BookLilli',
      },
      {
        text: 'Cares, LLC dba Carecal',
      },
      {
        text: ', a Rhode Island company.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'All other feedback, comments, requests for technical support, and other communications relating to the ',
      },
      {
        text: 'Site ',
      },
      {
        text: 'should be directed to: ',
      },
      {
        text: 'support@carecal.net',
        href: 'mailto:support@carecal.net',
      },
      {
        text: '.',
      },
    ],
  },
  {
    type: 'spacer',
  },
];
